// GENERATED from backend/services/billMemory/core — do not edit; run backend/scripts/sync-bill-memory-core.js
/**
 * Bill Memory — the match-name maker (source spec, "The building blocks", part 1).
 *
 * Every printed or typed name becomes its match name: lower case, pack-size text and
 * punctuation removed, simple plurals made singular, letters in any script kept. The same
 * function runs when a lesson is saved and when it is looked up, on the server and on the
 * screen, so the two can never disagree. The size is left out on purpose: quantity always
 * comes from the bill, so a new pack size is noticed rather than hidden.
 *
 * Pure: no I/O, no requires outside core/. Copied verbatim to the frontend.
 */

const UNIT_WORDS = [
  'kgs', 'kg', 'gms', 'gm', 'grams', 'gram', 'gr', 'g',
  'ltrs', 'ltr', 'lt', 'litres', 'litre', 'liters', 'liter', 'l', 'ml',
  'pcs', 'pc', 'pieces', 'piece', 'nos', 'no', 'pkt', 'packs', 'pack', 'tins', 'tin',
  'box', 'bottle', 'btl', 'dozen', 'doz',
];
const UNIT = `(?:${UNIT_WORDS.join('|')})`;
const NUM = '\\d+(?:\\.\\d+)?';

const PACK_OF = /pack\s+of\s+\d+/g;
const MULTIPACK = new RegExp(`${NUM}\\s*[x*×]\\s*${NUM}\\s*${UNIT}?(?![a-z])`, 'g');
const SIZE = new RegExp(`${NUM}(?:\\s*-\\s*${NUM})?\\s*${UNIT}(?![a-z])`, 'g');
const NOT_WORD = /[^\p{L}\p{N}\p{M}]+/gu;

/** Words that end in "s" but are not plurals. */
const PLURAL_KEEP = new Set(['madras', 'hummus', 'couscous', 'asparagus', 'molasses', 'nachos', 'swiss', 'octopus', 'citrus', 'lotus', 'cactus']);
/** Irregular plurals. */
const PLURAL_MAP = { leaves: 'leaf', loaves: 'loaf', knives: 'knife', halves: 'half' };

function singular(word) {
  if (PLURAL_MAP[word]) return PLURAL_MAP[word];
  if (PLURAL_KEEP.has(word) || !/^[a-z]+$/.test(word) || word.length <= 3) return word;
  if (/(ss|us|is)$/.test(word)) return word;
  if (/ies$/.test(word) && word.length > 4) return `${word.slice(0, -3)}y`;
  if (/(xes|ches|shes|oes)$/.test(word)) return word.slice(0, -2);
  if (/s$/.test(word)) return word.slice(0, -1);
  return word;
}

/** makeMatchName("Gc Tomato Puree 850gm") → "gc tomato puree". */
function makeMatchName(raw) {
  let s = String(raw == null ? '' : raw).normalize('NFC').toLowerCase();
  s = s.replace(PACK_OF, ' ').replace(MULTIPACK, ' ').replace(SIZE, ' ');
  s = s.replace(NOT_WORD, ' ');
  return s.split(' ').filter(Boolean).map(singular).join(' ');
}

/**
 * Drop a leading vendor item code ("45-ROCKET", "3002a Mutton Boneless", "LAIPL12 Onion") for the
 * dictionary, candidates and today's matcher. Vendor lessons keep the full match name. A plain number
 * followed by a single word ("7 Up", "65 Chicken") is part of the name and is kept, and so is a word
 * with one digit ("PS4", "B12").
 */
function stripItemCode(matchName, raw) {
  const tokens = String(matchName || '').split(' ').filter(Boolean);
  if (tokens.length < 2) return tokens.join(' ');
  const first = tokens[0];
  const rest = tokens.slice(1).join(' ');
  if (/^\d{1,5}[a-z]$/.test(first)) return rest;
  if (/^[a-z]{2,}\d{2,}$/.test(first)) return rest; // letters then 2+ digits: a vendor's code (Fix 13)
  if (/^\d{1,5}$/.test(first)) {
    if (/^\s*\d{1,5}\s*-\s*\S/.test(String(raw || ''))) return rest;
    if (tokens.length >= 3) return rest;
  }
  return tokens.join(' ');
}

/**
 * joinedName("pesara pappu") → "pesarapappu": a match name with every space dropped (Fix 11 part B). A
 * fallback only: tried when the normal name finds nothing, and never stored.
 */
function joinedName(matchName) {
  return String(matchName || '').replace(/\s+/g, '');
}

/**
 * soundKey("Kothimeera") → "kotimir": a rough key for names that sound alike (I15) — th→t, ph→f, sh→s,
 * z→j, w→v, ee→i, oo→u, doubled letters once, a final "a" after a consonant dropped. Only ever used to
 * suggest; never stored.
 */
function soundKey(name) {
  let s = joinedName(makeMatchName(name));
  s = s.replace(/th/g, 't').replace(/ph/g, 'f').replace(/sh/g, 's').replace(/z/g, 'j').replace(/w/g, 'v')
    .replace(/ee/g, 'i').replace(/oo/g, 'u');
  s = s.replace(/(.)\1+/gu, '$1');
  return s.replace(/([^aeiou])a$/, '$1');
}

export { makeMatchName, stripItemCode, singular, PLURAL_KEEP, PLURAL_MAP, UNIT_WORDS, joinedName, soundKey };
